import {ReadOnlyService} from '../../_services/read-only.service';
import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {StudentService} from '../../_services/student.service';
import {Router} from '@angular/router';
import moment from 'moment';
import {ToolsService} from '../../_services/tools.service';

declare let $: any;

@Component({
    selector: 'app-student-save',
    templateUrl: './student-save.component.html',
    styleUrls: ['./student-save.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StudentSaveComponent implements OnInit {

  studentId: string = this.router.url.split('/')[2];
  isNewStudent = false;
  studentForm = this.fb.group({
    id: [{value: null, disabled: true}],
    sexe: [{value: null, disabled: true}, Validators.required],
    formulaId: [{value: null, disabled: true}, Validators.required],
    neph: [{value: null, disabled: true}],
    firstname: [{value: null, disabled: true}, Validators.required],
    lastname: [{value: null, disabled: true}, Validators.required],
    email: [{value: null, disabled: true}, [Validators.required, Validators.email]],
    birthDate: [{value: null, disabled: true}, Validators.required],
    birthPlace: [{value: null, disabled: true}, Validators.required],
    phoneNumber: [{value: null, disabled: true}, [Validators.required, Validators.pattern('0[0-9]{9}')]],
    address: [{value: null, disabled: true}, Validators.required],
    postalCode: [{value: null, disabled: true}, Validators.required],
    city: [{value: null, disabled: true}, Validators.required],
    registrationDate: [{value: null, disabled: true}, Validators.required],
    evaluationDate: [{value: null, disabled: true}, Validators.required],
    evaluationHours: [{value: null, disabled: true}, Validators.required],
    agreedNumberHours: [{value: null, disabled: true}, Validators.required],
    contractDate: [{value: null, disabled: true}],
    settlementDate: [{value: null, disabled: true}],
    hasCode: [{value: null, disabled: true}]
  });
  formulas?: any[];
  studentFormula?: any;
  studentSnapshot?: any;
  canSubmit = false;
  isSaved = false;
  messageError = '';

  constructor(private fb: UntypedFormBuilder, private studentService: StudentService, private toolsService: ToolsService,
              private router: Router, public readOnlyService: ReadOnlyService) {

    studentService.getStudentsById(this.studentId).subscribe(
      student => {
        this.studentSnapshot = student;
        this.studentForm = this.getStudentForm(student);
        this.isNewStudent = student.status === 'TO_CREATE';
        this.studentForm.disable();
        this.studentFormula = student.formula;
        // Charger les formules après avoir récupéré la formule de l'élève
        this.loadFormulas();
      },
      err => {
        this.messageError = err.error.message;
      });

    $('.input-form').prop('disabled', true);
  }

  loadFormulas(): void {
    this.toolsService.getActiveFormulas().subscribe(
      formulas => {
        this.formulas = formulas;
        // Ajout de la formule de l'élève si absente
        if (this.studentFormula && !formulas.some((f: any) => f.id === this.studentFormula.id)) {
          if (this.formulas) {
            this.formulas.push({
              ...this.studentFormula,
              disabled: true // Marque comme désactivée
            });
          }
        }
      },
      err => {
        this.messageError = err.error.message;
      });
  }

  ngOnInit(): void {
  }

  // tslint:disable-next-line:typedef
  onEditStudent() {
    this.studentForm.markAllAsTouched();
    this.messageError = '';

    if (!this.normalizeDateFields()) {
      this.messageError = 'Merci de renseigner correctement les dates obligatoires du dossier.';
      return;
    }

    if (!this.studentForm.valid) {
      this.messageError = 'Merci de compléter les champs obligatoires avant d’enregistrer.';
      return;
    }

    const studentForm = this.studentForm.value;
    this.studentService.saveStudent(studentForm).subscribe(
      data => {
        this.messageError = '';
        this.isSaved = true;
        this.canSubmit = false;
        this.studentSnapshot = data;
        this.studentForm = this.getStudentForm(data);
        this.studentForm.disable();
        $('.input-form').prop('disabled', true);
      },
      err => {
        this.messageError = this.getDisplayErrorMessage(err?.error?.message);
      }
    );

    return true;
  }

  isSubmited(form: UntypedFormGroup) {
  }

  private getStudentForm(data: any) {
    this.studentForm.disable();
    return this.fb.group({
      id: data.id,
      sexe: data.sexe,
      formulaId: data.formula.id,
      neph: data.neph,
      firstname: data.firstname,
      lastname: data.lastname,
      email: [data.email],
      birthDate: this.formatDateValue(data.birthDate),
      birthPlace: data.birthPlace,
      phoneNumber: data.phoneNumber,
      address: data.address,
      postalCode: data.postalCode,
      city: data.city,
      registrationDate: this.formatDateValue(data.registrationDate),
      contractDate: this.formatDateValue(data.contractDate),
      evaluationDate: this.formatDateValue(data.evaluationDate),
      evaluationHours: [data.evaluationHours],
      agreedNumberHours: [data.agreedNumberHours],
      settlementDate: this.formatDateValue(data.settlementDate),
      hasCode: data.hasCode
    });
  }

  onEditActive() {
    this.studentForm.enable();
    this.isSaved = false;
    this.canSubmit = true;
    $('.input-form').removeAttr('disabled');
  }

  onCancelEdit() {
    if (!this.studentSnapshot) {
      return;
    }

    this.messageError = '';
    this.isSaved = false;
    this.canSubmit = false;
    this.studentForm = this.getStudentForm(this.studentSnapshot);
    this.studentForm.disable();
    $('.input-form').prop('disabled', true);
  }

  onBackToStudentDetails() {
    this.router.navigate(['/students', this.studentId]);
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.studentForm.get(controlName);
    return !!control && control.invalid && (control.touched || this.canSubmit);
  }

  private normalizeDateFields(): boolean {
    const requiredDateFields = ['birthDate', 'evaluationDate'];
    if (this.isNewStudent) {
      requiredDateFields.push('registrationDate');
    }

    for (const fieldName of requiredDateFields) {
      const control = this.studentForm.get(fieldName);
      const normalizedValue = this.normalizeDateValue(control?.value, false);
      control?.setValue(normalizedValue, {emitEvent: false});

      if (!normalizedValue) {
        control?.setErrors({required: true});
      }
    }

    this.normalizeOptionalDateField('contractDate');
    this.normalizeOptionalDateField('settlementDate');

    return requiredDateFields.every(fieldName => !!this.studentForm.get(fieldName)?.value);
  }

  private normalizeOptionalDateField(fieldName: string) {
    const control = this.studentForm.get(fieldName);
    const normalizedValue = this.normalizeDateValue(control?.value, true);
    control?.setValue(normalizedValue, {emitEvent: false});
  }

  private formatDateValue(value: string | null | undefined): string | null {
    return this.normalizeDateValue(value, true);
  }

  private normalizeDateValue(value: string | null | undefined, optional: boolean): string | null {
    if (value == null || value === '') {
      return optional ? null : null;
    }

    const normalizedDate = moment.utc(value, moment.ISO_8601, true);
    return normalizedDate.isValid() ? normalizedDate.format('YYYY-MM-DD') : null;
  }

  private getDisplayErrorMessage(message?: string): string {
    if (!message) {
      return 'Une erreur a été rencontrée lors de l’enregistrement du dossier.';
    }

    if (message.includes('Cannot deserialize value of type `java.time.LocalDate`')
      || message.includes('"Invalid date"')) {
      return 'Une date obligatoire est vide ou invalide. Merci de vérifier les champs de date du formulaire.';
    }

    return message;
  }

  uploadIdentificationFile(studentId: bigint) {
    this.studentService.uploadIdentificationFile(studentId).subscribe(
      data => {
        const file = new Blob([data], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      },
      error => {
        console.log('Error downloading the file.');
        console.log(error);
      });
  }

  uploadContract(studentId: bigint) {
    this.studentService.uploadContract(studentId).subscribe(
      data => {
        const file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      },
      error => {
        console.log('Error downloading the file.');
        console.log(error);
      });
  }
}
